import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  deserify,
  DocumentViewer,
  getMediaBasePath,
  IStyles,
  LinkButton,
  NoContentCard,
  pxToRem,
  pxTovW,
  theme,
} from '@geneo2-web/shared-ui';
import {
  FileEnum,
  QuestionPaperType,
} from '@protos/content_management/content.db_pb';
import { useAppSelector } from '../../../../reduxStore/reduxHooks';
import { TableShimmerQP } from './TableShimmerQP';
import { useState } from 'react';

const styles: IStyles = {
  tableContainerStyles: {
    flexGrow: 1,
    marginY: { xs: pxToRem(15), md: pxTovW(30) },
  },
  tableStyles: {
    width: '100%',
    marginTop: { xs: pxToRem(15), md: pxTovW(0) },
    marginBottom: { xs: pxToRem(15), md: pxTovW(30) },
    // height: { xs: '20vh', md: '20vh' },
  },
  headCells: {
    background: '#CDE6F9',
    color: '#1D1D1D',
    borderTop: '1px solid #1576c3',
    borderBottom: '1px solid #1576c3',
    fontWeight: '500',
    paddingY: { xs: pxToRem(5), md: pxTovW(20) },
    paddingX: { xs: pxToRem(5), md: pxTovW(25) },
    width: '50%',
  },
  bodyRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#F3F9FD',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#ffffff',
    },
  },
  bodyCell: {
    padding: { xs: pxToRem(5), md: pxTovW(20) },
  },

  link: {
    p: '0',
  },
  linkTypoStyle: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
};

interface IProps {
  loadingState: 'loading' | 'success' | 'error';
  pageNumber: number;
}
export const QuestionPaperTable = (props: IProps) => {
  const { loadingState, pageNumber } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { question_paper_data } = deserify(
    useAppSelector((state) => state.homeDashboard)
  );

  const [pdfURL, setPdfURL] = useState<string | undefined>(undefined);
  const [pdfName, setPdfName] = useState<string | undefined>(undefined);

  // ------------------------------------------------------------

  const getTableData = () => {
    if (question_paper_data?.selected_tab === QuestionPaperType.PREVIOUS_YEAR) {
      return question_paper_data?.previous_year_list?.[pageNumber];
    }
    return question_paper_data?.sample_paper_list?.[pageNumber] ?? [];
  };

  // ------------------------------------------------------------

  if (loadingState === 'error')
    return (
      <NoContentCard
        variant="error"
        icon="cards"
        text="Data could not be fetched."
        rootStyle={{ mt: { xs: pxToRem(20), md: pxTovW(50) } }}
      />
    );

  if (loadingState === 'success' && getTableData()?.length === 0)
    return (
      <NoContentCard
        variant="info"
        icon="cards"
        text={
          question_paper_data?.selected_tab === QuestionPaperType.PREVIOUS_YEAR
            ? 'No Previous Year Question Papers Available'
            : 'No Sample Papers Available'
        }
        rootStyle={{ mt: { xs: pxToRem(20), md: pxTovW(50) } }}
      />
    );

  return (
    <TableContainer sx={styles.tableContainerStyles}>
      <Table stickyHeader aria-label="sticky table" sx={styles.tableStyles}>
        <TableHead>
          <TableRow>
            {[
              question_paper_data?.selected_tab ===
                QuestionPaperType.PREVIOUS_YEAR && isMobile
                ? 'QUESTION PAPERS'
                : question_paper_data?.selected_tab ===
                    QuestionPaperType.PREVIOUS_YEAR && !isMobile
                ? 'PREVIOUS YEAR QUESTION PAPERS'
                : 'SAMPLE PAPERS',
              'ANSWER SHEETS',
            ].map((column, index) => (
              <TableCell key={index} sx={styles.headCells}>
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  textAlign={isMobile ? 'center' : 'left'}
                >
                  {column}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {loadingState === 'loading' ? (
          <TableShimmerQP column={2} rows={7} />
        ) : (
          <TableBody>
            {getTableData()?.map((row, index) => (
              <TableRow sx={styles.bodyRow}>
                <TableCell sx={styles.bodyCell}>
                  <LinkButton
                    textColor="primary.main"
                    onClick={() => {
                      setPdfURL(getMediaBasePath(row.questionPaperUrl));
                      setPdfName(row.questionPaperName);
                    }}
                    rootStyle={styles.link}
                    typoStyle={styles.linkTypoStyle}
                  >
                    {row.questionPaperName}
                  </LinkButton>
                </TableCell>

                <TableCell sx={styles.bodyCell}>
                  <LinkButton
                    textColor="primary.main"
                    onClick={() => {
                      setPdfURL(getMediaBasePath(row.answerSheetUrl));
                      setPdfName(row.answerSheetName);
                    }}
                    rootStyle={styles.link}
                    typoStyle={styles.linkTypoStyle}
                  >
                    {row.answerSheetName}
                  </LinkButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        )}

        {pdfURL && (
          <DocumentViewer
            downloadUrl={pdfURL}
            previewUrl={pdfURL}
            gcpUrl={pdfURL}
            type={FileEnum.FILE_TYPE_PDF}
            fileName={pdfName}
            open={!!pdfURL}
            closeFile={() => {
              setPdfURL(undefined);
              setPdfName(undefined);
            }}
          />
        )}
      </Table>
    </TableContainer>
  );
};
